import React, { useState, useRef, useEffect, useMemo, useContext } from 'react';
import { Icon } from '@iconify/react';
import { ConfigContext } from '../../context/GlobalContext';
import { Languages } from '../../Utils/Languages';

const BusinessDropdown = ({
    businesses,
    selectedCompanyId,
    onBusinessChange,
    isAllowedCheck,
    onPricingNavigate,
    disabled = false,
    showAllOption = false
}) => {
    const { state } = useContext(ConfigContext);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const ref = useRef(null);

    // Theme detection
    const isDark = localStorage.getItem("theme") === "dark";

    // Close dropdown on click outside
    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    // Filter businesses based on search input
    const filtered = useMemo(() => {
        let list = businesses;
        if (search.trim()) {
            const s = search.toLowerCase();
            list = businesses.filter(b =>
                b.companyName?.toLowerCase().includes(s) ||
                b.ownerName?.toLowerCase().includes(s)
            );
        }
        return list;
    }, [businesses, search]);

    const selected = businesses.find(b => b.companyId === selectedCompanyId);
    const isAllSelected = selectedCompanyId === "null" || selectedCompanyId === null;

    // Theme-based styles
    const dropdownBg = isDark ? '#1b2431' : '#fff';
    const menuBorder = isDark ? '1px solid #334155' : 'none';
    const inputBg = isDark ? '#2b3548' : '#F8FAFC';
    const inputColor = isDark ? '#f1f5f9' : 'inherit';
    const textColor = isDark ? '#f1f5f9' : '#475569';
    const hoverBg = isDark ? '#334155' : '#F1F5F9';

    return (
        <div ref={ref} className="dropdown custom-business-dropdown" style={{ position: 'relative' }}>
            <button
                className={`btn btn-outline-primary-600 not-active px-18 py-11 dropdown-toggle toggle-icon d-flex align-items-center justify-content-between gap-2 rounded ${disabled ? 'opacity-75 cursor-not-allowed' : ''}`}
                type="button"
                onClick={() => !disabled && setOpen(prev => !prev)}
                style={{
                    minWidth: '250px',
                    maxWidth: '300px',
                    transition: 'all 0.2s ease',
                    pointerEvents: disabled ? 'none' : 'auto'
                }}
            >
                <span className="text-truncate" style={{ fontSize: '14px', fontWeight: '500', maxWidth: '240px' }}>
                    {isAllSelected ? (Languages[state?.LANGUAGE]?.All_Business || 'All Business') : (
                        selected ? (
                            selected.isShared
                                ? `${selected.companyName} (Shared by: ${selected.ownerName})`
                                : selected.companyName
                        ) : 'Select Business'
                    )}
                </span>
            </button>

            {open && (
                <ul className="dropdown-menu show w-100 mt-1 shadow-lg rounded-8 text-start p-0"
                    style={{
                        zIndex: 1050,
                        minWidth: '100%',
                        position: 'absolute',
                        left: 0,
                        backgroundColor: dropdownBg,
                        border: menuBorder
                    }}>
                    <div className={`px-12 py-8 sticky-top border-bottom`} style={{ backgroundColor: dropdownBg, borderColor: isDark ? '#334155' : '#dee2e6' }}>
                        <div className="position-relative">
                            <Icon
                                icon="lucide:search"
                                className="position-absolute top-50 translate-middle-y ms-12"
                                style={{ fontSize: '16px', color: isDark ? '#94a3b8' : '#64748b' }}
                            />
                            <input
                                type="text"
                                className={`form-control ps-32 ${isDark ? 'border-secondary-light' : ''}`}
                                placeholder="Search business..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                autoFocus
                                style={{
                                    fontSize: '13px',
                                    backgroundColor: inputBg,
                                    color: inputColor,
                                    border: isDark ? '1px solid #334155' : '1px solid #dee2e6'
                                }}
                            />
                        </div>
                    </div>
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }} className="hide-scrollbar">
                        {showAllOption && !search && (
                            <li key="all-businesses">
                                <button
                                    className={`dropdown-item px-16 py-8 rounded d-flex align-items-center gap-2 text-secondary-light bg-hover-neutral-200 text-hover-neutral-900
                                        ${isAllSelected ? 'bg-primary-50 fw-semibold text-primary-600' : ''}`}
                                    onClick={() => {
                                        onBusinessChange("null");
                                        setOpen(false);
                                    }}
                                    style={{
                                        fontSize: '14px',
                                        color: isAllSelected ? '#487fff' : textColor,
                                        backgroundColor: isAllSelected ? (isDark ? '#1e293b' : '#f0f4ff') : 'transparent'
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = hoverBg}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = isAllSelected ? (isDark ? '#1e293b' : '#f0f4ff') : 'transparent'}
                                >
                                    <Icon icon="lucide:layers" className="text-lg" />
                                    <span className="flex-grow-1">{Languages[state?.LANGUAGE]?.All_Business || "All Business"}</span>
                                    {isAllSelected && (
                                        <Icon icon="lucide:check" className="text-primary-600 text-lg ms-auto" />
                                    )}
                                </button>
                            </li>
                        )}
                        {filtered.length > 0 ? (
                            filtered.map(b => {
                                const isAllowed = isAllowedCheck ? isAllowedCheck(b) : true;
                                const isSelected = b.companyId === selectedCompanyId;
                                return (
                                    <li key={b.companyId}>
                                        <button
                                            className={`dropdown-item px-16 py-8 rounded d-flex align-items-center gap-2 text-secondary-light bg-hover-neutral-200 text-hover-neutral-900
                                                ${isSelected ? 'bg-primary-50 fw-semibold text-primary-600' : ''}`}
                                            onClick={() => {
                                                if (isAllowed) {
                                                    onBusinessChange(b.companyId);
                                                    setOpen(false);
                                                    setSearch("");
                                                } else if (onPricingNavigate) {
                                                    onPricingNavigate();
                                                }
                                            }}
                                            style={{
                                                fontSize: '14px',
                                                color: isSelected ? '#487fff' : textColor,
                                                backgroundColor: isSelected ? (isDark ? '#1e293b' : '#f0f4ff') : 'transparent'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!disabled) e.currentTarget.style.backgroundColor = hoverBg;
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!disabled) e.currentTarget.style.backgroundColor = isSelected ? (isDark ? '#1e293b' : '#f0f4ff') : 'transparent';
                                            }}
                                        >
                                            <span className="d-flex align-items-center gap-2 flex-grow-1 overflow-hidden">
                                                {!isAllowed && (
                                                    <i className="fas fa-crown" style={{ fontSize: "16px", color: "gold" }} />
                                                )}
                                                <div className="d-flex flex-column overflow-hidden text-start">
                                                    <span className="text-truncate">{b.companyName}</span>
                                                    {b.isShared && (
                                                        <small style={{ fontSize: '11px', color: isDark ? '#94a3b8' : '#64748b' }}>
                                                            (Shared by: {b.ownerName})
                                                        </small>
                                                    )}
                                                </div>
                                            </span>
                                            {isSelected && (
                                                <Icon icon="lucide:check" className="text-primary-600 text-lg ms-auto" />
                                            )}
                                        </button>
                                    </li>
                                );
                            })
                        ) : (
                            <li className={`px-16 py-10 text-center small italic`} style={{ color: isDark ? '#94a3b8' : '#64748b' }}>No businesses found</li>
                        )}
                    </div>
                </ul>
            )}
        </div>
    );
};

export default BusinessDropdown;
